// This is the React component for Section 10 ("How Do We Grow?")
// 1. A waterfall reveal for the main title area.
// 2. DESKTOP: A CSS-only hover effect for the 3 cards.
// 3. MOBILE: A GSAP scroll-reveal for the 3 cards.

import React, { useLayoutEffect, useRef } from "react";
import "./Section10.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the 3 card images
import springImg from "../../../assets/images/spring.png";
import fallImg from "../../../assets/images/fall.png";
import winterImg from "../../../assets/images/winter.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text & Data ---
const cardsData = [
  {
    title: "SPRING - SUMMER",
    text: "Begin pilot with community collaborators",
    image: springImg,
    overlayClass: "overlay-1", // For the green color
  },
  {
    title: "FALL",
    text: "Reflect and review - continue pilot",
    image: fallImg,
    overlayClass: "overlay-2", // For the orange color
  },
  {
    title: "WINTER",
    text: "Co-develop activity cards and a shareable toolkit",
    image: winterImg,
    overlayClass: "overlay-3", // For the blue color
  },
];

// --- React Component ---
function Section10() {
  const sectionRef = useRef(null);
  // Refs for the title area
  const titleRef = useRef(null);
  const yearRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  // Refs for the cards
  const cardRefs = useRef([]);

  useLayoutEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, cardsData.length);

    const ctx = gsap.context(() => {
      // --- Animation 1: The Title Area (Waterfall) ---
      const tlTitle = gsap.timeline({
        scrollTrigger: {
          trigger: titleRef.current,
          // --- FIX: Trigger later ---
          start: "top 70%",
          toggleActions: "play none none none",
          once: true,
        },
      });

      // Title & Year grow/fade in
      tlTitle.fromTo(
        [titleRef.current, yearRef.current],
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.8, ease: "power2.out", stagger: 0.1 }
      );
      // Lines "grow outwards" from the center
      tlTitle.fromTo(
        [line1Ref.current, line2Ref.current],
        { scaleX: 0 }, // Start at 0 width
        // --- FIX: No stagger ---
        { scaleX: 1, duration: 0.8, ease: "power2.out" },
        "-=0.5" // Overlap with title animation
      );

      // --- Animation 2: The Card Reveals (Mobile/Tablet ONLY) ---
      ScrollTrigger.matchMedia({
        
        // --- A. DESKTOP: Simple fade-in ---
        // The hover animation is handled by CSS.
        "(min-width: 1024px)": function () {
          gsap.fromTo(
            cardRefs.current,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              stagger: 0.2,
              duration: 0.8,
              ease: "power2.out",
              scrollTrigger: {
                trigger: cardRefs.current[0],
                start: "top 80%",
                toggleActions: "play none none none",
                once: true,
              },
            }
          );
        },

        // --- B. MOBILE/TABLET: Animate on Scroll ---
        "(max-width: 1023px)": function () {
          cardRefs.current.forEach((card) => {
            const cardTitle = card.querySelector(".section-10-card-title");
            const cardCopy = card.querySelector(".section-10-card-copy");
            const cardOverlay = card.querySelector(".section-10-card-overlay");

            const tlCard = gsap.timeline({
              scrollTrigger: {
                trigger: card,
                start: "top 60%", // When card is 60% from top
                toggleActions: "play none none none",
                once: true, // Only run this once on mobile
              },
            });

            // The "Reveal" animation sequence
            tlCard
              .to(cardTitle, {
                // 1. Move title from center to top
                top: "3rem", // Final position
                y: "0%", // Final transform
                duration: 0.6,
                ease: "power2.out",
              })
              .to(
                cardOverlay,
                {
                  // 2. Fade out overlay
                  opacity: 0,
                  duration: 0.6,
                  ease: "power2.out",
                },
                "-=0.4" // Overlap
              )
              .to(
                cardCopy,
                {
                  // 3. Fade in copy
                  opacity: 1,
                  top: "6rem", // <-- ADD THIS (tells it where to move to)
                    y: "0%",
                  duration: 0.5,
                  ease: "power2.out",
                },
                "-=0.4" // Overlap
              );
          });
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="section-10" ref={sectionRef}>
      
      {/* --- Title Area --- */}
      <div className="section-10-title-area">
        <h2 className="section-10-title" ref={titleRef}>
          How Do We Grow?
        </h2>
        <div className="section-10-date" ref={yearRef}>
          <span className="section-10-line" ref={line1Ref}></span>
          <span className="section-10-year">2025-2026</span>
          <span className="section-10-line" ref={line2Ref}></span>
        </div>
      </div>

      {/* --- Cards Container --- */}
      <div className="section-10-cards-container">
        {cardsData.map((card, index) => (
          <div
            className="section-10-card"
            key={index}
            ref={(el) => (cardRefs.current[index] = el)}
          >
            {/* The text content is now ON TOP of the image */}
            <div className="section-10-card-content">
              <h3 className="section-10-card-title">{card.title}</h3>
              <p className="section-10-card-copy">{card.text}</p>
            </div>
            
            {/* The image and overlay are in the background */}
            <div className="section-10-card-image-container">
              <img
                src={card.image}
                alt={card.title}
                className="section-10-card-image"
              />
              <div
                className={`section-10-card-overlay ${card.overlayClass}`}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Section10;